import React, { useState } from 'react'
import * as yup from 'yup'
import { Link, useHistory } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'

import Breadcrumb from '~/components/Breadcrumb'
import { Row, Col, Show, LoadableCard } from '~/components/commons'
import { Card, CardBody, CardTitle, Button, Input } from 'reactstrap'
import FilterPanel from './commons/FilterPanel'
import ScheduleSubmitForm from './commons/ScheduleSubmitForm'
import Field from './commons/Field'
import validations from './commons/validations'

import { Container, Rule, Helper, AddNewButton } from './styles'
import * as Icon from 'react-feather'
import { queries } from '~/constants/mediaQuery'
import styled from 'styled-components'
import { Formik, FieldArray } from 'formik'
import Textarea from '~/components/Textarea'
import { useSelector } from 'react-redux'
import api from '~/services/api'
import { toast } from 'react-toastify'
import { Alert } from '~/components/Alert'

CardContainer.Body = styled(CardBody)`
  padding: 2.5rem;
  overflow-x: auto;
  overflow: visible;
  @media ${queries.less.xl} {
    padding: 1.5rem;
  }
`

const initialValues = {
  questionsAndAnswers: [{ questionText: '', answerText: '' }],
  locationNames: [],
}

const validationSchema = () =>
  yup.object().shape({
    questionsAndAnswers: validations.questionsAndAnswers,
    locationNames: validations.locationNames,
  })

function CardContainer({ children, title }) {
  return (
    <Card>
      <CardContainer.Body>
        <Show condition={!!title}>
          <CardTitle>
            <FormattedMessage id={title} />
          </CardTitle>
        </Show>
        {children}
      </CardContainer.Body>
    </Card>
  )
}

function QuestionsAndAnswersForm({ indice, arrayHelpers }) {
  return (
    <div className="position-relative pt-1">
      <Button.Erase
        className="btn-icon rounded-circle p-50"
        outline
        color="danger"
        onClick={() => arrayHelpers.remove(indice)}
      >
        <Icon.Trash2 size={16} />
      </Button.Erase>
      <Field
        name={`questionsAndAnswers[${indice}].questionText`}
        render={Input}
        className="mb-2"
        label="Question"
        required
      />
      <Field
        name={`questionsAndAnswers[${indice}].answerText`}
        label="Answer"
        render={Textarea}
        maxLength={4096}
        resize="true"
        required
        enableVariables
      />
      <Rule />
    </div>
  )
}

function AnsweredQuestions() {
  const [alertVisible, setAlertVisible] = useState(false)
  const [submitting, setSubmitting] = React.useState(false)
  const gmbAccountId = useSelector(
    state => state.user?.selectedProject?.gmbAccountId
  )
  const projectId = useSelector(state => state.user?.selectedProject?.id)

  const intl = useIntl()
  const history = useHistory()

  async function handleValidateSubmit({ questionsAndAnswers, locationNames }) {
    setSubmitting(true)
    try {
      await api.post(`/projects/${projectId}/answered-question`, {
        questionsAndAnswers,
        locationNames,
        gmbAccountId,
      })

      setAlertVisible(true)
    } catch (error) {
      toast.error('Error creating your questions, try again later.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <Container>
        <Breadcrumb
          paths={[
            {
              render: () => (
                <Link className="d-flex align-items-center" to="/">
                  <Icon.Home size={20} />
                </Link>
              ),
            },
            {
              render: () => (
                <strong className="text-primary font-medium-3">
                  <FormattedMessage id="Informations" />
                </strong>
              ),
            },
            {
              render: () => (
                <span className="font-medium-3">
                  <FormattedMessage id="FAQ" />
                </span>
              ),
            },
          ]}
        />

        <LoadableCard loading={submitting}>
          <Row>
            <Formik
              initialValues={initialValues}
              onSubmit={handleValidateSubmit}
              validationSchema={validationSchema}
              validateOnBlur={false}
              validateOnChange={false}
              validateOnMount={false}
            >
              {({ values, submitForm }) => (
                <>
                  <Col md={6}>
                    <CardContainer
                      title="Bulk Questions & Answers"
                      style={{ paddingTop: '40px' }}
                    >
                      <FieldArray
                        name="questionsAndAnswers"
                        render={arrayHelpers => (
                          <div
                            style={{ display: 'flex', flexDirection: 'column' }}
                          >
                            {values.questionsAndAnswers.map((question, i) => (
                              <QuestionsAndAnswersForm
                                key={`question-${i}`}
                                indice={i}
                                arrayHelpers={arrayHelpers}
                              />
                            ))}
                            <Helper>
                              {intl.formatMessage({ id: 'FAQ Helper' })}
                            </Helper>
                            <AddNewButton
                              onClick={() =>
                                arrayHelpers.push({
                                  questionText: '',
                                  answerText: '',
                                })
                              }
                              color="primary"
                              className="capitalize"
                            >
                              <FormattedMessage id="Add new" />
                            </AddNewButton>
                          </div>
                        )}
                      />
                    </CardContainer>
                  </Col>
                  <Col md={6}>
                    <CardContainer>
                      <FilterPanel />
                      <ScheduleSubmitForm
                        onSubmit={submitForm}
                        loading={submitting}
                      />
                    </CardContainer>
                  </Col>
                </>
              )}
            </Formik>
          </Row>
        </LoadableCard>
        <Alert
          type="success"
          visible={alertVisible}
          onClose={() => history.push('/')}
          confirmBtnText="Go to dashboard"
          body="Questions successfully created!"
          title="Success"
        />
      </Container>
    </>
  )
}

Button.Erase = styled(Button)`
  position: absolute;
  top: -10px;
  right: 0;
`

export default AnsweredQuestions
